/**
 * 应用程序主布局组件
 */

import React, { useState, useMemo } from 'react';
import { Layout, Menu, Typography, theme, Dropdown, Avatar, Space } from 'antd';
import {
  DashboardOutlined,
  UploadOutlined,
  FileTextOutlined,
  AlertOutlined,
  SettingOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  CloudUploadOutlined,
  AuditOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MENU_ITEMS } from '../../utils/constants';
import logo from '../../assets/logo.png';
import jiazhi from '../../assets/jiazhi.png';

const { Header, Sider, Content, Footer } = Layout;
const { Title } = Typography;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout: authLogout } = useAuth();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 处理登出
  const handleLogout = () => {
    authLogout();
    navigate('/login');
  };

  // 根据用户角色过滤菜单项
  const filteredMenuItems = useMemo(() => {
    const userRole = user?.role || 'user';

    const filterItems = (items: typeof MENU_ITEMS) => {
      return items.filter(item => {
        // 检查当前项目是否允许当前用户角色访问
        if (item.roles && !item.roles.includes(userRole)) {
          return false;
        }

        // 如果有子项，递归过滤子项
        if (item.children) {
          const filteredChildren = item.children.filter(child =>
            !child.roles || child.roles.includes(userRole)
          );
          // 如果所有子项都被过滤掉了，那么父项也不显示
          return filteredChildren.length > 0;
        }

        return true;
      }).map(item => ({
        ...item,
        children: item.children?.filter(child =>
          !child.roles || child.roles.includes(userRole)
        )
      }));
    };

    return filterItems(MENU_ITEMS);
  }, [user?.role]);

  // 图标映射
  const iconMap: Record<string, React.ReactNode> = {
    DashboardOutlined: <DashboardOutlined />,
    UploadOutlined: <UploadOutlined />,
    FileTextOutlined: <FileTextOutlined />,
    AlertOutlined: <AlertOutlined />,
    SettingOutlined: <SettingOutlined />,
    CloudUploadOutlined: <CloudUploadOutlined />,
    AuditOutlined: <AuditOutlined />,
    UserOutlined: <UserOutlined />,
  };

  // 转换菜单项格式
  const menuItems = filteredMenuItems.map((item) => ({
    key: item.key,
    icon: iconMap[item.icon],
    label: item.label,
    children: item.children?.map((child) => ({
      key: child.key,
      label: child.label,
    })),
  }));

  // 处理菜单点击
  const handleMenuClick = (info: any) => {
    const findPath = (items: typeof MENU_ITEMS, key: string): string | null => {
      for (const item of items) {
        if (item.key === key && item.path) {
          return item.path;
        }
        if (item.children) {
          for (const child of item.children) {
            if (child.key === key) {
              return child.path;
            }
          }
        }
      }
      return null;
    };

    const path = findPath(MENU_ITEMS, info.key);
    if (path) {
      navigate(path);
    }
  };

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const path = location.pathname;
    
    // 直接匹配路径
    for (const item of MENU_ITEMS) {
      if (item.path === path) {
        return [item.key];
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.path === path) {
            return [child.key];
          }
        }
      }
    }
    
    // 如果没有直接匹配，尝试部分匹配
    if (path.startsWith('/management')) {
      if (path.includes('/cities')) return ['cities'];
      if (path.includes('/businesses')) return ['businesses'];
      if (path.includes('/elements')) return ['elements'];
      if (path.includes('/standards')) return ['standards'];
    }
    
    return ['dashboard'];
  };

  // 获取展开的菜单项
  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/management')) {
      return ['management'];
    }
    if (path.startsWith('/training-')) {
      return ['training-management'];
    }
    if (path.startsWith('/faculty')) {
      return ['faculty-management'];
    }
    return [];
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div style={{
          height: 32,
          margin: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          // borderRadius: 8,
        }}>
          <img 
            src={logo} 
            alt="Logo" 
            style={{ 
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }} 
          />
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'trigger',
              onClick: () => setCollapsed(!collapsed),
              style: { fontSize: '18px', cursor: 'pointer' },
            })}
            <Title level={4} style={{ margin: '0 0 0 24px' }}>
              培训费用标准化检查与预警平台
            </Title>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'logout',
                    label: '退出登录',
                    icon: <LogoutOutlined />,
                    onClick: handleLogout,
                  },
                ],
              }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: '6px' }}>
                <Avatar size="small" icon={<UserOutlined />} />
                <span style={{ color: '#666' }}>
                  {user?.username || '用户'}
                  {user?.role === 'admin' && ' (管理员)'}
                </span>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '24px',
            padding: '24px',
            minHeight: 'calc(100vh - 200px)',
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            overflow: 'auto',
            marginBottom: '80px',
          }}
        >
          {children}
        </Content>
        <Footer
          style={{
            textAlign: 'center',
            padding: '16px 24px',
            background: colorBgContainer,
            borderTop: '1px solid #f0f0f0',
            position: 'fixed',
            bottom: 0,
            left: collapsed ? 80 : 200,
            right: 0,
            transition: 'left 0.2s',
            zIndex: 1000,
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 16
          }}>
            <img 
              src={jiazhi} 
              alt="华普亿方价值观" 
              style={{ 
                height: 24, 
                objectFit: 'contain',
                filter: 'opacity(0.8)'
              }} 
            />
          </div>
        </Footer>
      </Layout>
    </Layout>
  );
};

export default AppLayout;